import { Constants } from '../common/Constants';
import { PushType } from '../common/EnumTypes';
import * as InternalTypes from '../common/InternalTypes';
import { Logger } from '../common/Logger';
import { LoggerTexts } from '../common/LoggerTexts';
import * as Types from '../common/Types';
import { Util } from '../common/Util';
import { CentralConfig } from '../config/CentralConfig';
import { AppPushConfig } from '../config/push/AppPushConfig';
import { InstantPushConfig } from '../config/push/InstantPushConfig';
import { AppPushState } from '../data/AppPushState';
import { InstantPushConfigState } from '../data/InstantPushConfigState';
import { UserState } from '../data/UserState';
import { StorageService } from '../storage/StorageService';

export class ValidationHandler {
	private _centralConfig: CentralConfig;
	private _storage: StorageService;
	private _logger: Logger;

	get storage(): StorageService {
		return this._storage;
	}

	get centralConfig(): CentralConfig {
		return this._centralConfig;
	}

	init(storage: StorageService, centralConfig: CentralConfig) {
		if (!storage) {
			throw new Error(LoggerTexts.VALIDATION_HANDLER.CTOR_PARAM_STORAGE_NOT_PROVIDED);
		}
		if (!centralConfig) {
			throw new Error(LoggerTexts.VALIDATION_HANDLER.CTOR_PARAM_CENTRAL_CFG_NOT_PROVIDED);
		}
		this._storage = storage;
		this._centralConfig = centralConfig;
		this._logger = new Logger(Constants.FILE_VALIDATION_HANDLER);
	}

	validate(pushData: Types.PushFeedbackRequestData, pushType: PushType): InternalTypes.StateConfig | undefined {
		if (this._isPushDataValid(pushData)) {
			return this._getRelevantStateConfig(pushData.areaId, pushData.triggerName, pushType);
		}
		return undefined;
	}

	_isPushDataValid(pushData: Types.PushFeedbackRequestData): boolean {
		if (pushData.areaId && pushData.triggerName) {
			const sAreaId = pushData.areaId;
			const sTriggerName = pushData.triggerName;
			if (!Util.isString(sAreaId) || Util.stringIsEmpty(sAreaId)) {
				this._logger.error(`Event Payload data is invalid - Area ID "${sAreaId}" is invalid.`);
				return false;
			}
			if (!Util.isString(sTriggerName) || Util.stringIsEmpty(sTriggerName)) {
				this._logger.error(`Event Payload data is invalid - Trigger Name "${sTriggerName}" is invalid.`);
				return false;
			}
		} else {
			this._logger.error('Event Payload data is invalid!');
			return false;
		}
		return true;
	}

	private _getRelevantStateConfig(areaId: string, triggerName: string, pushType: PushType): InternalTypes.StateConfig | undefined {
		const matchingConfig = this._collectConfigurationsForTrigger(areaId, triggerName, pushType);
		if (matchingConfig) {
			const stateConfig = this._combineRelatedStateConfig(matchingConfig, pushType);
			if (stateConfig) {
				this._increaseTriggeredCount(stateConfig, pushType);
				return this._filterQualifiedConfig(stateConfig);
			}
		}
		return undefined;
	}

	private _collectConfigurationsForTrigger(areaId: string, triggerName: string, pushType: PushType) {
		const pushConfig = this._centralConfig.pushConfig;
		let featurePushConfig: AppPushConfig | InstantPushConfig | undefined;
		if (pushConfig) {
			if (pushType === PushType.appPush) {
				featurePushConfig = pushConfig.findAppPushConfigById(areaId, triggerName);
			} else {
				featurePushConfig = pushConfig.findInstantPushConfigById(areaId, triggerName);
			}
			if (featurePushConfig) {
				return featurePushConfig;
			}
		}
		return undefined;
	}

	private _combineRelatedStateConfig(configuration: AppPushConfig | InstantPushConfig, pushType: PushType): InternalTypes.StateConfig | undefined {
		const currentStateConfig: InternalTypes.StateConfig = {} as InternalTypes.StateConfig;
		const { areaId, triggerName } = configuration as AppPushConfig | InstantPushConfig;
		const triggerType = configuration['triggerType'];
		currentStateConfig.combinedKey = configuration.combinedKey;
		currentStateConfig.config = configuration;
		let state = this._findStateById(areaId, triggerName, pushType);
		if (!state) {
			const userState = Util.readUserState(this._storage);
			if (userState) {
				if (pushType === PushType.appPush) {
					state = userState.createAndAddAppPushState(areaId, triggerName, triggerType);
				} else {
					state = userState.createAndAddInstantPushState(areaId, triggerName, Date.now());
				}
				this._saveUserState(userState);
			}
		}
		if (state) {
			currentStateConfig.state = state;
			return currentStateConfig;
		}
		return undefined;
	}

	private _findStateById(areaId: string, triggerName: string, pushType: PushType): AppPushState | InstantPushConfigState | undefined {
		const userState = Util.readUserState(this._storage);
		if (userState) {
			if (pushType === PushType.appPush) {
				return userState.getAppPushState(areaId, triggerName);
			} else {
				return userState.getInstantPushConfigState(areaId, triggerName);
			}
		}
		return undefined;
	}

	private _increaseTriggeredCount(stateConfig: InternalTypes.StateConfig, pushType: PushType) {
		if (stateConfig.config) {
			const userState = Util.readUserState(this._storage);
			let pushState: AppPushState | InstantPushConfigState | undefined;
			if (userState) {
				if (pushType === PushType.appPush) {
					pushState = userState.getAppPushState(stateConfig.config.areaId, stateConfig.config.triggerName);
				} else {
					pushState = userState.getInstantPushConfigState(stateConfig.config.areaId, stateConfig.config.triggerName);
				}
				if (pushState) {
					pushState.increaseTriggeredCount();
					this._saveUserState(userState);
				}
			}
		}
	}

	private _saveUserState(userState: UserState) {
		if (userState) {
			this._storage.saveUserState(userState);
		}
	}

	private _filterQualifiedConfig(stateConfig: InternalTypes.StateConfig): InternalTypes.StateConfig | undefined {
		if (stateConfig && stateConfig.state && stateConfig.config) {
			if (stateConfig.config instanceof AppPushConfig) {
				if (stateConfig.config.isQualifiedForPush(stateConfig.state as AppPushState)) {
					return stateConfig;
				}
			} else if (stateConfig.config instanceof InstantPushConfig) {
				if (stateConfig.config.isQualifiedForPush(stateConfig.state as InstantPushConfigState)) {
					return stateConfig;
				}
			}
		}
		return undefined;
	}
}
